/**
 * Metadata encryption/decryption for SkySend.
 *
 * Metadata (file names, sizes, MIME types) is encrypted with AES-256-GCM
 * using the metaKey derived from HKDF and a random 12-byte IV.
 *
 * The metadata is serialized as JSON, encoded to UTF-8, then encrypted.
 * The IV is stored separately (in the database) alongside the ciphertext.
 *
 * Security notes:
 * - A fresh random IV is generated for each metadata encryption
 * - The IV does not need to be secret, only unique per encryption
 * - GCM provides both confidentiality and authenticity
 */
import { randomBytes, asBytes } from "./util.js";
/** IV length for metadata encryption (12 bytes for AES-GCM). */
export const META_IV_LENGTH = 12;
/**
 * Encrypt file metadata with AES-256-GCM.
 *
 * @param metadata - The file metadata to encrypt
 * @param metaKey - The AES-256-GCM key derived for metadata
 * @returns The encrypted ciphertext and the random IV
 */
export async function encryptMetadata(metadata, metaKey) {
    const encoder = new TextEncoder();
    const plaintext = encoder.encode(JSON.stringify(metadata));
    const iv = randomBytes(META_IV_LENGTH);
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: asBytes(iv), tagLength: 128 }, metaKey, asBytes(plaintext));
    return {
        ciphertext: new Uint8Array(ciphertext),
        iv,
    };
}
/**
 * Decrypt file metadata from AES-256-GCM ciphertext.
 *
 * @param ciphertext - The encrypted metadata
 * @param iv - The IV used during encryption
 * @param metaKey - The AES-256-GCM key derived for metadata
 * @returns The decrypted and parsed file metadata
 * @throws If decryption fails (wrong key, tampered data) or JSON is invalid
 */
export async function decryptMetadata(ciphertext, iv, metaKey) {
    if (iv.length !== META_IV_LENGTH) {
        throw new Error(`Metadata IV must be exactly ${META_IV_LENGTH} bytes`);
    }
    let plaintext;
    try {
        plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: asBytes(iv), tagLength: 128 }, metaKey, asBytes(ciphertext));
    }
    catch {
        throw new Error("Metadata decryption failed - data may be corrupted or tampered with");
    }
    const decoder = new TextDecoder();
    const json = decoder.decode(plaintext);
    let parsed;
    try {
        parsed = JSON.parse(json);
    }
    catch {
        throw new Error("Metadata decryption produced invalid JSON");
    }
    return validateMetadata(parsed);
}
/**
 * The byte size the decrypted file stream must have, to pass to `createDecryptStream`.
 *
 * GCM authenticates each record on its own, so a server can drop whole records from
 * the end of the stream and the rest still decrypts. The metadata is authenticated
 * with the metaKey, so its size is the one the server cannot change. Undefined only
 * for archives uploaded by older clients, which carry no archive size.
 */
export function expectedPlaintextSize(metadata) {
    return metadata.type === "single" ? metadata.size : metadata.archiveSize;
}
/** Validate that parsed JSON conforms to the FileMetadata shape. */
function validateMetadata(data) {
    if (typeof data !== "object" || data === null) {
        throw new Error("Invalid metadata: not an object");
    }
    const obj = data;
    if (obj.type === "single") {
        if (typeof obj.name !== "string" || obj.name.length === 0) {
            throw new Error("Invalid metadata: missing or empty file name");
        }
        if (typeof obj.size !== "number" || obj.size < 0) {
            throw new Error("Invalid metadata: invalid file size");
        }
        if (typeof obj.mimeType !== "string") {
            throw new Error("Invalid metadata: missing MIME type");
        }
        return {
            type: "single",
            name: obj.name,
            size: obj.size,
            mimeType: obj.mimeType,
        };
    }
    if (obj.type === "archive") {
        if (!Array.isArray(obj.files)) {
            throw new Error("Invalid metadata: files must be an array");
        }
        const files = [];
        for (const file of obj.files) {
            if (typeof file !== "object" || file === null) {
                throw new Error("Invalid metadata: file entry must be an object");
            }
            const f = file;
            if (typeof f.name !== "string" || f.name.length === 0) {
                throw new Error("Invalid metadata: file entry missing name");
            }
            if (typeof f.size !== "number" || f.size < 0) {
                throw new Error("Invalid metadata: file entry invalid size");
            }
            files.push({ name: f.name, size: f.size });
        }
        if (typeof obj.totalSize !== "number" || obj.totalSize < 0) {
            throw new Error("Invalid metadata: invalid total size");
        }
        if (obj.archiveSize !== undefined &&
            (typeof obj.archiveSize !== "number" ||
                !Number.isSafeInteger(obj.archiveSize) ||
                obj.archiveSize < 0)) {
            throw new Error("Invalid metadata: invalid archive size");
        }
        return {
            type: "archive",
            files,
            totalSize: obj.totalSize,
            ...(obj.archiveSize !== undefined ? { archiveSize: obj.archiveSize } : {}),
        };
    }
    throw new Error("Invalid metadata: unknown type");
}
//# sourceMappingURL=metadata.js.map